import { message } from 'antd';
import axios, { AxiosInstance } from 'axios';
import { isArray, isEmpty } from 'underscore';
import { IAuthenticate, ICurrentUser, IError } from './../typings/api.d';
import Auth from './Auth';
import DataPreview from './DataPreview';
import Pages from './Pages';
import Search from './Search';
import SearchDetail from './SearchDetail';
import Service from './Service';
import Price from './Price';
import ShoppingCart from './Shopping';
import DownloadRequest from './DownloadRequest';
import FileEndPoint from './FileEndPoint';
import ChartRequest from './Chart';
import BSApi from './BSApi';
import PreCheckout from './PreCheckout';
import MoodV2Order from './MoodV2Order';
import FWEPoint from './FWEPoint'
import Metadata from './Metadata';
import Notifications from './Notifications';
import CurrentUser from './CurrentUser';
import { IConfig } from '../typings/config';
import getConfig from './config';
import EventEmitter from 'events';
import InsituPoint from './insituPoint';

export const initApis = async () => {
  if (!Api.SERVER_DOMAIN) {
    const json: IConfig = await getConfig();
    if (json.serverDomain) {
      Api.init(
        json.serverDomain,
        json.iisWebServerSiteBackend,
        json.urlVersion1,
        json.meshDomain,
        json.cloudAdminUrl,
        json.fweUrl,
        json.useNewSpectraEndpoint && json.useNewSpectraEndpoint === "true" ? true : false,
        json.signalRServer,
        json.dhiSupportUrl
      );
    }
  }
};
export const apiEventEmitter = new EventEmitter();

export let axiosInstance: AxiosInstance;

export default class Api {
  static SERVER_DOMAIN = '';
  static MESH_DOMAIN = '';
  static IISSite = '';
  static VERSION1_URL = '';
  static CLOUD_ADMIN_URL = '';
  static FWE_URL = '';
  static UseNewSpectraEndpoint = false;
  static SignalRServer = '';
  static DhiSupportUrl = '';
  static XSRF_TOKEN = '';

  static init(
    serverDomain: string,
    iisWebServerSiteBackend: string,
    urlVersion1: string,
    meshDomain: string,
    cloudAdminUrl: string,
    fweUrl: string,
    useNewSpectraEndpoint: boolean,
    signalRServer: string,
    dhiSupportUrl: string
  ) {
    axios.defaults.baseURL = serverDomain + iisWebServerSiteBackend + "/api";
    axios.defaults.headers.post['Content-Type'] = 'application/json';
    Api.SERVER_DOMAIN = serverDomain;
    Api.MESH_DOMAIN = meshDomain ?? serverDomain;
    Api.VERSION1_URL = urlVersion1;
    Api.IISSite = iisWebServerSiteBackend;
    Api.CLOUD_ADMIN_URL = cloudAdminUrl;
    Api.FWE_URL = fweUrl;
    Api.UseNewSpectraEndpoint = useNewSpectraEndpoint;
    Api.SignalRServer = signalRServer
    Api.DhiSupportUrl = dhiSupportUrl

    axiosInstance = axios.create();

    axiosInstance.interceptors.response.use(
      (response) => {

        if (isArray(response.data) || response.data) {
          return response;
        }
        if (response.data.Error === null) {
          return response;
        } else {
          const knownError: IError = response.data.Error as IError;
          // errors with code 1 are expected errors and will be handled in the calling component
          if (!isEmpty(knownError) && knownError.Code === 1) {
            return response;
          } else {
            return Promise.reject(response.data.Error);
          }
        }
      },
      (error) => {
        if (error.response) {
          const status = error.response.status;
          if (status === 401) {
            apiEventEmitter.emit('credentialError', error.response.status);
          } else {
            const knownError: IError = error as IError;
            if (!isEmpty(knownError) && knownError.ErrorMessage) {
              doError(knownError.ErrorMessage);
            } else if(error.message){
              doError(error.message);
            } else {
              doError(JSON.stringify(error));
            }
          }
        } else {
          doError(JSON.stringify(error));
        }
        return Promise.reject(error);
      }
    );
  }

  static initializeToken(token: string) {
    this.XSRF_TOKEN = token;
    axiosInstance.interceptors.request.use(
      (config) => {
        if (['post'].includes(config.method?.toLowerCase() || '')) {
          if (Api.XSRF_TOKEN) {
            if (!config.headers) {
              config.headers = {};
            }
            config.headers['X-XSRF-TOKEN'] = Api.XSRF_TOKEN;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  static clearForgeryToken() {
    this.XSRF_TOKEN = '';
  }
  static detail = new SearchDetail();
  static service = new Service();
  static search = new Search();
  static auth = new Auth();
  static dataPreview = new DataPreview();
  static pages = new Pages();
  static price = new Price();
  static shopping = new ShoppingCart();
  static downloadRequest = new DownloadRequest();
  static fileEndPoint = new FileEndPoint();
  static chart = new ChartRequest();
  static bsApi = new BSApi();
  static preCheckout = new PreCheckout();
  static moodV2Order = new MoodV2Order();
  static FWEPoint = new FWEPoint();
  static insituPoint = new InsituPoint()
  static metadata = new Metadata();
  static notifications = new Notifications();
  static currentUser = new CurrentUser();
}

export function doError(error: string) {
  message.error(error);
}

